import { useEffect, useState } from 'react';
import './App.css';
import AuthRouter from './navigation/authNav';
import NoAuthRouter from './navigation/noAuthNav';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter, useSearchParams } from 'react-router-dom';
import Navbar from './components/Navbar/navbar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store/store';
import { refreshAuth } from './store/Slices/authSlice';
import { ThemeProvider } from '@emotion/react';
import { createTheme } from '@mui/material';
import { PRIMARY_COLOR, SECONDARY_COLOR } from './constants';
import ActivityIndicator from './components/activityIndicator';
import { getLocalSelectedAddress } from './store/Slices/cartSlice';
import CancelableAlert from './components/CancelableAlert';
import { LANDSCAPE_ALERT_CONTENT, LANDSCAPE_ALERT_TITLE } from './constants/longStrings.constants';

function RootNav({ loading, isLoggedIn }: { loading: boolean; isLoggedIn: boolean }) {
  const [params, setParams] = useSearchParams();

  const token = params.get('token');
  const rcn = params.get('rcn');

  if (token && rcn) {
    window.localStorage.setItem('token', token);
    window.localStorage.setItem('rcn', rcn);
    setParams({}); // Clear the URL parameters
    window.history.replaceState({}, document.title, window.location.pathname); // Remove the parameters from the URL
    window.location.reload();
  }

  // This component is used to conditionally render the AuthRouter or NoAuthRouter based on the loading and isLoggedIn state.
  return <>{loading ? <ActivityIndicator /> : isLoggedIn ? <AuthRouter /> : <NoAuthRouter />}</>;
}

function App() {
  const { isAuthenticated, userId } = useSelector((state: RootState) => state.auth);
  const [landscapeAlertopen, setLandscapeAlertOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const updateStore = async (id: any) => {
    dispatch(refreshAuth());
    dispatch(getLocalSelectedAddress());

    if (window?.innerHeight / 1.5 < window?.innerWidth) {
      setLandscapeAlertOpen(true);
    }
  };

  const handleClose = () => {
    setLandscapeAlertOpen(false);
  };

  useEffect(() => {
    setIsLoggedIn(isAuthenticated);
    const _RCN = window.localStorage.getItem('rcn');
    updateStore(userId ?? _RCN);
    setLoading(false);
    // eslint-disable-next-line
  }, [isAuthenticated, userId]);

  const theme = createTheme({
    typography: {
      fontFamily: 'Manrope, Lato, sans-serif',
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
    },
    palette: {
      primary: {
        main: PRIMARY_COLOR,
        light: PRIMARY_COLOR,
        dark: PRIMARY_COLOR,
        contrastText: '#fff',
      },
      secondary: {
        main: SECONDARY_COLOR,
        light: SECONDARY_COLOR,
        dark: SECONDARY_COLOR,
        contrastText: '#fff',
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div
        id="webapp-container"
        style={{
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
        className="h-screen flex flex-col container mx-auto"
      >
        <BrowserRouter>
          <Navbar />
          <div className="mt-12 flex flex-col flex-1">
            <RootNav loading={loading} isLoggedIn={isLoggedIn} />
          </div>
          <ToastContainer />
        </BrowserRouter>
      </div>
      <CancelableAlert
        open={landscapeAlertopen}
        handleClose={handleClose}
        title={LANDSCAPE_ALERT_TITLE}
        content={LANDSCAPE_ALERT_CONTENT}
      />
    </ThemeProvider>
  );
}

export default App;
